import React, { useState, useEffect } from 'react';
import { Calendar, Users, TrendingUp, Plus, AlertCircle } from 'lucide-react';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';
import AbsencesList from './AbsencesList';
import AbsenceForm from './AbsenceForm';
import AbsenceDetails from './AbsenceDetails';
import AbsenceCalendar from './AbsenceCalendar';
import AbsenceStats from './AbsenceStats';
import { useAbsences } from '../../../hooks/useAbsences';
import { absencesAPI } from '../../../api/absences';

export default function AbsencesModule({ currentUser, deepLinkId, onDeepLinkConsumed }) {
  const { can } = usePermissionsContext();
  const [activeTab, setActiveTab] = useState('list');
  const [showForm, setShowForm] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [viewingAbsence, setViewingAbsence] = useState(null); // solo lectura desde deep-link
  const [filters, setFilters] = useState({
    estado: '',
    tipo: '',
    employeeId: ''
  });

  const {
    absences,
    types,
    balance,
    loading,
    error,
    loadAbsences,
    loadTypes,
    loadBalance,
    createAbsence,
    aprobar,
    rechazar,
    cancelar
  } = useAbsences();

  useEffect(() => {
    loadAbsences(filters);
    loadTypes();
  }, []);

  // Deep-link: abrir vista de detalle (solo lectura) desde historial de aprobaciones
  useEffect(() => {
    if (!deepLinkId) return;
    absencesAPI.getById(deepLinkId)
      .then(data => {
        if (data.success && data.absence) {
          setViewingAbsence(data.absence);
          onDeepLinkConsumed?.();
        }
      })
      .catch(() => { /* silencio */ });
  }, [deepLinkId]); // eslint-disable-line

  useEffect(() => {
    loadAbsences(filters);
  }, [filters]);

  useEffect(() => {
    if (currentUser?.employee_id) {
      loadBalance(currentUser.employee_id);
    }
  }, [currentUser]);

  const handleCreateAbsence = async (data, documentos) => {
    const result = await createAbsence(data, documentos);
    if (result.success) {
      setShowForm(false);
      setSelectedAbsence(null);
      loadAbsences(filters);
    }
    return result;
  };

  const handleAprobar = async (id, comentarios) => {
    const result = await aprobar(id, comentarios);
    if (result.success) loadAbsences(filters);
    return result;
  };

  const handleRechazar = async (id, motivo) => {
    const result = await rechazar(id, motivo);
    if (result.success) loadAbsences(filters);
    return result;
  };

  const handleCancelar = async (id, motivo) => {
    const result = await cancelar(id, motivo);
    if (result.success) loadAbsences(filters);
    return result;
  };

  const canApprove = can('VACATION_APPROVE');

  const TABS = [
    { id: 'list',     label: 'Lista',         icon: Users      },
    { id: 'calendar', label: 'Calendario',    icon: Calendar   },
    { id: 'stats',    label: 'Estadísticas',  icon: TrendingUp },
  ];

  return (
    <div className="space-y-6">

      {/* Toolbar: tabs + botón */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <button
          onClick={() => { setSelectedAbsence(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={14} />
          Nueva Solicitud
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Error al cargar datos</p>
            <p className="text-xs mt-0.5 opacity-80">{error}</p>
          </div>
        </div>
      )}

      {/* Contenido */}
      {activeTab === 'list' && (
        <AbsencesList
          absences={absences}
          loading={loading}
          error={error}
          currentUser={currentUser}
          filters={filters}
          setFilters={setFilters}
          types={types}
          onEdit={(absence) => { setSelectedAbsence(absence); setShowForm(true); }}
          onAprobar={canApprove ? handleAprobar : null}
          onRechazar={canApprove ? handleRechazar : null}
          onCancelar={handleCancelar}
        />
      )}

      {activeTab === 'calendar' && (
        <AbsenceCalendar
          absences={absences}
          loading={loading}
          currentUser={currentUser}
        />
      )}

      {activeTab === 'stats' && (
        <AbsenceStats
          absences={absences}
          types={types}
          loading={loading}
          currentUser={currentUser}
        />
      )}

      {/* Modal formulario (crear / editar) */}
      {showForm && (
        <AbsenceForm
          absence={selectedAbsence}
          types={types}
          currentUser={currentUser}
          onSubmit={handleCreateAbsence}
          onClose={() => { setShowForm(false); setSelectedAbsence(null); }}
        />
      )}

      {/* Modal detalle solo lectura (desde historial de aprobaciones) */}
      {viewingAbsence && (
        <AbsenceDetails
          absence={viewingAbsence}
          onClose={() => setViewingAbsence(null)}
        />
      )}
    </div>
  );
}
